/* =============================================================
   MAGON WISHLIST — localStorage + drawer + share + convert
   ============================================================= */

const STORAGE_KEY = 'magon:wishlist:v1';
const TTL_DAYS = 14;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

class MagonWishlist {
  constructor() {
    this.dialog = document.getElementById('magon-wishlist');
    this.listEl = this.dialog?.querySelector('[data-wishlist-list]');
    this.emptyEl = this.dialog?.querySelector('[data-wishlist-empty]');
    this.footerEl = this.dialog?.querySelector('[data-wishlist-footer]');
    this.countEl = this.dialog?.querySelector('[data-wishlist-footer-count]');
    this.headerCountEls = document.querySelectorAll('[data-wishlist-count]');
    this.tpl = document.getElementById('magon-wishlist-item-template');

    this.items = this._read();
    this._cleanup();
    this._render();
    this._bindUI();
    this._syncToggleButtons();

    // Sync entre tabs
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) {
        this.items = this._read();
        this._render();
        this._syncToggleButtons();
      }
    });
  }

  // ── Storage (com TTL) ──
  _read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed.items)) return [];
      // Expirou? Limpa
      if (parsed.savedAt && (Date.now() - parsed.savedAt > TTL_MS)) {
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }
      return parsed.items;
    } catch {
      return [];
    }
  }

  _write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        items: this.items,
        savedAt: Date.now()
      }));
    } catch (e) {
      console.warn('Wishlist storage failed:', e);
    }
  }

  // ── Cleanup de items inválidos ──
  _cleanup() {
    this.items = this.items.filter(it => it && it.id && it.variantId && it.title);
  }

  // ── API pública ──
  add(item) {
    if (!item.id || !item.variantId) return false;
    const exists = this.items.some(it => String(it.variantId) === String(item.variantId));
    if (exists) return false;
    this.items.unshift({ ...item, addedAt: Date.now() });
    this._write();
    this._render();
    this._syncToggleButtons();
    return true;
  }

  remove(variantId) {
    const before = this.items.length;
    this.items = this.items.filter(it => String(it.variantId) !== String(variantId));
    if (this.items.length === before) return false;
    this._write();
    this._render();
    this._syncToggleButtons();
    return true;
  }

  toggle(item) {
    const exists = this.items.some(it => String(it.variantId) === String(item.variantId));
    return exists ? this.remove(item.variantId) === false ? false : false : this.add(item);
  }

  has(variantId) {
    return this.items.some(it => String(it.variantId) === String(variantId));
  }

  count() { return this.items.length; }

  open() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    requestAnimationFrame(() => {
      this.dialog.querySelector('.magon-wishlist__close')?.focus();
    });
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  // ── Render ──
  _render() {
    if (!this.dialog) return;

    const count = this.items.length;

    // Bubble no header
    this.headerCountEls.forEach(el => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });

    // Estado do drawer
    if (count === 0) {
      this.emptyEl?.removeAttribute('hidden');
      this.listEl?.setAttribute('hidden', '');
      this.footerEl?.setAttribute('hidden', '');
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.listEl?.removeAttribute('hidden');
    this.footerEl?.removeAttribute('hidden');
    if (this.countEl) this.countEl.textContent = count;

    if (!this.listEl || !this.tpl) return;

    this.listEl.innerHTML = '';
    const frag = document.createDocumentFragment();
    this.items.forEach(it => {
      const node = this.tpl.content.cloneNode(true);
      const article = node.querySelector('[data-wishlist-item]');
      article.dataset.variantId = it.variantId;

      node.querySelectorAll('[data-wl-link]').forEach(a => {
        a.setAttribute('href', it.url || `/products/${it.handle || ''}`);
      });

      const img = node.querySelector('[data-wl-image]');
      if (img) {
        img.src = it.image || '';
        img.alt = it.title || '';
      }

      node.querySelector('[data-wl-title]').textContent = it.title || '';

      const variantEl = node.querySelector('[data-wl-variant]');
      if (it.variantTitle && variantEl) {
        variantEl.textContent = it.variantTitle;
      } else if (variantEl) {
        variantEl.remove();
      }

      const priceEl = node.querySelector('[data-wl-price]');
      if (priceEl) priceEl.textContent = this._formatPrice(it.price);

      frag.appendChild(node);
    });
    this.listEl.appendChild(frag);
  }

  _formatPrice(cents) {
    if (cents == null) return '';
    const num = typeof cents === 'string' ? parseFloat(cents) : cents;
    return (num / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // ── Sync de botões coração na PDP/cards ──
  _syncToggleButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const variantId = btn.dataset.wlVariant;
      const isSaved = this.has(variantId);
      btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      btn.setAttribute('aria-label', isSaved ? 'Remover da lista de desejos' : 'Adicionar à lista de desejos');
      const labelAdd = btn.querySelector('[data-wl-label-add]');
      const labelRem = btn.querySelector('[data-wl-label-remove]');
      labelAdd?.toggleAttribute('hidden', isSaved);
      labelRem?.toggleAttribute('hidden', !isSaved);
    });
  }

  // ── UI bindings ──
  _bindUI() {
    // Open
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-action="open-wishlist"]');
      if (openBtn) { e.preventDefault(); this.open(); return; }

      const closeBtn = e.target.closest('[data-action="close-wishlist"]');
      if (closeBtn) { this.close(); return; }

      const browseBtn = e.target.closest('[data-action="close-wishlist-and-browse"]');
      if (browseBtn) { this.close(); return; }

      // Toggle (heart) na PDP/card
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        const item = this._readToggleData(toggleBtn);
        if (this.has(item.variantId)) this.remove(item.variantId);
        else this.add(item);
        return;
      }

      // Remove no item do drawer
      const removeBtn = e.target.closest('[data-action="wishlist-remove"]');
      if (removeBtn) {
        const article = removeBtn.closest('[data-wishlist-item]');
        const vid = article?.dataset.variantId;
        if (vid) this.remove(vid);
        return;
      }

      // Compartilhar
      const shareBtn = e.target.closest('[data-action="share-wishlist"]');
      if (shareBtn) { this._share(); return; }

      // Converter em carrinho
      const cartBtn = e.target.closest('[data-action="wishlist-to-cart"]');
      if (cartBtn) { this._convertToCart(); return; }
    });

    // ESC fecha
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dialog?.hasAttribute('open')) {
        this.close();
      }
    });

    // Click no backdrop (::backdrop) fecha
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
  }

  _readToggleData(btn) {
    return {
      id: btn.dataset.wlId,
      handle: btn.dataset.wlHandle,
      variantId: btn.dataset.wlVariant,
      title: btn.dataset.wlTitle,
      price: parseInt(btn.dataset.wlPrice, 10) || 0,
      url: btn.dataset.wlUrl,
      image: btn.dataset.wlImage,
      variantTitle: btn.dataset.wlVariantTitle || ''
    };
  }

  // ── Compartilhar (Web Share API + fallback copy) ──
  async _share() {
    const url = this._cartPermalinkURL();
    const text = `Olha o que separei na Magon Concepth: ${this.items.length} ${this.items.length === 1 ? 'item' : 'itens'}.`;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Minha lista Magon', text, url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }

    // Fallback: copia o link
    try {
      await navigator.clipboard.writeText(url);
      this._toast('Link copiado para a área de transferência');
    } catch {
      // Último fallback: prompt
      window.prompt('Copie o link abaixo:', url);
    }
  }

  // ── Converter wishlist em carrinho ──
  _convertToCart() {
    if (this.items.length === 0) return;
    window.location.href = this._cartPermalinkURL();
  }

  _cartPermalinkURL() {
    // Permalink Shopify: /cart/VARIANT:QTY,VARIANT:QTY...
    const path = this.items.map(it => `${it.variantId}:1`).join(',');
    return `${window.location.origin}/cart/${path}`;
  }

  _toast(msg) {
    const el = document.createElement('div');
    el.textContent = msg;
    el.style.cssText = `
      position:fixed; bottom:24px; left:50%; transform:translateX(-50%);
      background:#191919; color:#fff; padding:12px 20px;
      font-family:var(--font-accent--family,sans-serif); font-size:12px;
      letter-spacing:0.15em; text-transform:uppercase; z-index:9999;
      box-shadow:0 8px 24px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2400);
  }
}

// Init quando DOM ready (idempotente)
const initWishlist = () => {
  if (window.MagonWishlistInstance) return;
  window.MagonWishlistInstance = new MagonWishlist();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist);
} else {
  initWishlist();
}
